import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { SectionArrow } from '../Icons/Svg/SectionArrow';
import { Linkedin } from '../Icons/Svg/Linkedin';
import {
  advisors,
  SECRET_NAME,
  SECRET_INDEX_NAME,
  type Advisor,
  type AdvisorSocial,
} from './metadata';
import maleSilhouette from '../../assets/images/male_profile_silhouette.webp';
import femaleSilhouette from '../../assets/images/female_profile_silhouette.webp';

// Silhouette images by gender — shown when `revealed: false`. Kept here
// rather than in metadata since it's image-asset wiring, not editor copy.
const SILHOUETTE_BY_GENDER: Record<'male' | 'female', string> = {
  male: maleSilhouette,
  female: femaleSilhouette,
};

// Display label used inside each card's name slot. Replaces the real name
// for secret variants so the identity stays hidden.
const displayName = (a: Advisor): string =>
  a.revealed === false ? SECRET_NAME : a.name;

// Display label used in the bottom-row index. Separate from `displayName` so
// the H2 inside the card and the small label under the progress bar can use
// different wording.
const displayIndexName = (a: Advisor): string =>
  a.revealed === false ? SECRET_INDEX_NAME : a.name;

// Shows the silhouette matching the advisor's gender for secret variants;
// falls back to the real photo for revealed advisors (or if gender is unset).
const displayImage = (a: Advisor): string =>
  a.revealed === false && a.gender ? SILHOUETTE_BY_GENDER[a.gender] : a.imageUrl;

interface AdvisorCaruselProps {
  // 'hover' (default): icons appear when the active advisor's name is hovered.
  // 'always': icons are always visible next to the name.
  socialDisplay?: 'always' | 'hover';
}

const XGlyph = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const SocialIcons: React.FC<{
  social?: AdvisorSocial;
  visible: boolean;
  iconSize?: number;
}> = ({ social, visible, iconSize = 14 }) => {
  if (!social) return null;
  const has = social.linkedin || social.twitter;
  if (!has) return null;

  return (
    <div
      className={`flex items-end pb-[0.3em] pl-[8px] justifu-center transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]
        max-mobile:pb-[1.5em] max-mobile:pl-[0px] max-mobile:items-center ${
        visible
          ? 'opacity-100 scale-100'
          : 'opacity-0 scale-50 pointer-events-none'
      }`}
    >
      {social.linkedin && (
        <a
          href={social.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="transition-all duration-150 hover:scale-105"
          aria-label="LinkedIn"
          title="LinkedIn"
        >
          <span
          >
            <Linkedin className="w-[24px] h-auto" />
          </span>
        </a>
      )}
      {social.twitter && (
        <a
          href={social.twitter}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="p-[0.25em] rounded text-[var(--BlackBrand-br-5)] hover:text-[var(--BlackBrand)] transition-all duration-150 hover:scale-110"
          aria-label="X / Twitter"
          title="X / Twitter"
        >
          <XGlyph size={iconSize - 1} />
        </a>
      )}
    </div>
  );
};

// Per-field ghost content — locks each text block (name / description / bio)
// to its own longest value. Using a single advisor's combined-length winner
// is unsafe because the longest description and the longest bio belong to
// different advisors; the wrong ghost lets the active slide overflow it.
const tallestAdvisor = {
  name: advisors.reduce((max, a) =>
    a.name.length > max.name.length ? a : max
  ).name,
  description: advisors.reduce((max, a) =>
    a.description.length > max.description.length ? a : max
  ).description,
  bio: advisors.reduce((max, a) =>
    a.bio.length > max.bio.length ? a : max
  ).bio,
};

const EXIT_DURATION = 0.25;
const ENTER_DURATION = 0.85;
const EXIT_EASE = 'power2.in';
const ENTER_EASE = 'expo.out';
const SLIDE_DISTANCE = 28;

const AdvisorCarusel: React.FC<AdvisorCaruselProps> = ({
  socialDisplay = 'hover',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isNameHovered, setIsNameHovered] = useState(false);
  // "isMobile" here means tablet + mobile (≤1024px) — the desktop layout is
  // reserved for true desktop only.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 1024px)').matches
      : false
  );
  const textRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const mobileNameRef = useRef<HTMLDivElement | null>(null);
  const mobileDescRef = useRef<HTMLDivElement | null>(null);
  const mobileBioRef = useRef<HTMLDivElement | null>(null);
  const isAnimatingRef = useRef(false);

  const pendingIndexRef = useRef<number | null>(null);
  const pendingDirRef = useRef<'next' | 'prev'>('next');

  // Coalesced click intent. Updated synchronously on every click; the active
  // transition reads it on completion to chain to the latest target. This
  // lets users mash next/prev rapidly without dropping clicks or interrupting
  // the in-flight animation — every click is honored, sequentially.
  const intentRef = useRef(0);
  const intentDirRef = useRef<'next' | 'prev'>('next');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Preload all advisor images so the <img src> swap during a slide change
  // hits the cache instantly — without this, the IMG paints a blank frame
  // mid-fade-in while the new file loads, causing a visible flicker.
  // We also call `.decode()` to force the browser to decode the bitmap into
  // GPU memory now; otherwise a freshly-fetched-but-undecoded image can still
  // produce a one-frame blank during the first transition to it.
  useEffect(() => {
    advisors.forEach((advisor) => {
      const img = new Image();
      img.src = displayImage(advisor);
      img.decode().catch(() => {
        /* decode is best-effort; failures here just mean we fall back to
           on-demand decoding when the slide actually mounts */
      });
    });
  }, []);

  // GSAP targets — the mobile layout splits the name out of the text wrapper,
  // so include the name ref too. Filter drops whichever layout isn't mounted.
  const getTargets = () => {
    const els: (Element | null)[] = [
      textRef.current,
      imageRef.current,
      mobileNameRef.current,
      mobileDescRef.current,
      mobileBioRef.current,
    ];
    return els.filter((el): el is Element => el !== null);
  };

  // Holds the latest version of the EXIT-phase function so the ENTER
  // onComplete (set up inside an effect) can chain into a fresh transition
  // without a stale closure.
  const runTransitionRef = useRef<() => void>(() => {});

  runTransitionRef.current = () => {
    const target = intentRef.current;
    if (target === currentIndex) {
      isAnimatingRef.current = false;
      return;
    }
    isAnimatingRef.current = true;

    const dir = intentDirRef.current;
    const offset = dir === 'next' ? -SLIDE_DISTANCE : SLIDE_DISTANCE;
    const targets = getTargets();

    // Phase 1: animate out the OLD content. Plain `opacity` (not autoAlpha)
    // — no visibility toggle, avoids compositing jitter.
    gsap.to(targets, {
      xPercent: offset,
      opacity: 0,
      duration: isMobile ? 0.3 : EXIT_DURATION,
      ease: EXIT_EASE,
      stagger: isMobile ? 0 : 0.04,
      onComplete: () => {
        // Queue the index swap; the useEffect below runs the entrance once React commits.
        pendingIndexRef.current = target;
        pendingDirRef.current = dir;
        setCurrentIndex(target);
      },
    });
  };

  // Phase 2: after React commits the new content, animate it in. When the
  // entrance finishes, if the user has clicked again during the transition
  // (intentRef has moved on from currentIndex), fire another full transition
  // — sequential, not interrupting, so the beautiful tween is never cut off.
  useEffect(() => {
    if (pendingIndexRef.current === null) return;
    pendingIndexRef.current = null;

    const offset =
      pendingDirRef.current === 'next' ? -SLIDE_DISTANCE : SLIDE_DISTANCE;
    const targets = getTargets();

    // Single atomic fromTo: GSAP applies the "from" state and runs the tween in
    // one operation, removing any chance of an intermediate paint between the
    // exit-end state and the entrance start.
    gsap.fromTo(
      targets,
      { xPercent: -offset, opacity: 0 },
      {
        xPercent: 0,
        opacity: 1,
        duration: isMobile ? 0.5 : ENTER_DURATION,
        ease: ENTER_EASE,
        stagger: isMobile ? 0 : 0.06,
        onComplete: () => {
          isAnimatingRef.current = false;
          // Chain to the latest intent if it moved during this transition.
          if (intentRef.current !== currentIndex) {
            runTransitionRef.current();
          }
        },
      }
    );
  }, [currentIndex, isMobile]);

  const goNext = () => {
    intentRef.current = (intentRef.current + 1) % advisors.length;
    intentDirRef.current = 'next';
    if (!isAnimatingRef.current) runTransitionRef.current();
  };

  const goPrev = () => {
    intentRef.current =
      (intentRef.current - 1 + advisors.length) % advisors.length;
    intentDirRef.current = 'prev';
    if (!isAnimatingRef.current) runTransitionRef.current();
  };

  // Direct-target click (e.g. bottom indicator). Direction is computed
  // relative to the latest intent so the slide moves the visually correct way.
  const goTo = (target: number) => {
    if (target === intentRef.current) return;
    intentDirRef.current = target > intentRef.current ? 'next' : 'prev';
    intentRef.current = target;
    if (!isAnimatingRef.current) runTransitionRef.current();
  };

  const current = advisors[currentIndex];

  return (
    <div className="w-full flex flex-col">
      {/* Header — section pill + index */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col flex-1">
          <div className="h-[0.0625em] bg-[var(--WhiteBrand-br-92)] w-full my-[0.625em]" />
          <div className="flex flex-row justify-between">
          <p className="text-md font-main font-[500] text-[var(--GoldBrand-dr-2)] uppercase tracking-[0.05em] flex items-center gap-[0.5em]">
            <span className="inline-block w-[0.375em] h-[0.375em] rounded-full bg-[var(--GoldBrand-dr-2)]" />
            Advisory Board
          </p>
          <span className="text-md font-main text-[var(--BlackBrand-br-5)] tracking-[0.05em] ml-[2em]
          max-tablet:hidden">
          {String(currentIndex + 1).padStart(2, '0')} /{' '}
          {String(advisors.length).padStart(2, '0')}
        </span>
        </div>
        </div>
        
      </div>

      {/* Slide stage — desktop / tablet (mobile has its own layout below) */}
      {!isMobile && (
      <div
        className="grid grid-cols-[1fr_0.5fr] gap-[5dvw] items-stretch mt-[32px]
         max-tablet:gap-[32px]"
      >
        {/* Text column — content centered. Nav buttons live under the image (right column). */}
        <div className="flex flex-col h-full min-h-0">
          {/* Stacking grid — ghost (tallest advisor) locks the height; active overlays */}
          <div className="grid flex-1 min-h-0">
            {/* Ghost — invisible, sized to the tallest advisor */}
            <div
              aria-hidden="true"
              className="[grid-area:1/1] flex flex-col items-start justify-center invisible pointer-events-none"
            >
              <h2
                className="text-lg40 font-deco font-[580] leading-[1.2] text-balance mb-[24px]
                max-tablet:text-lg32
                max-mobile:text-lg24 max-mobile:mb-[16px]"
              >
                {tallestAdvisor.name}
              </h2>
              <p
                className="w-full mb-[32px] text-lg18 font-main font-[500] leading-[1.5]
                max-mobile:text-base max-mobile:mb-[16px]"
              >
                {tallestAdvisor.description}
              </p>
              <div
                className="columns-2 gap-[2em] w-full
                max-tablet:columns-1 max-tablet:gap-0"
              >
                <p
                  className="text-base font-main font-[400] leading-[1.6] whitespace-pre-line
                  max-mobile:text-md"
                >
                  {tallestAdvisor.bio}
                </p>
              </div>
            </div>

            {/* Active — visible, animated by GSAP */}
            <div
              ref={textRef}
              className="[grid-area:1/1] flex flex-col will-change-transform items-start justify-center"
            >
              <div
                className="flex items-end gap-[0.6em] flex-wrap mb-[24px]
                max-mobile:mb-[16px]"
                onMouseEnter={() => setIsNameHovered(true)}
                onMouseLeave={() => setIsNameHovered(false)}
              >
                <h2
                  className="text-lg40 font-deco font-[580] leading-[1.2] text-[var(--BlackBrand)] text-balance
                  max-tablet:text-lg32
                  max-mobile:text-lg24"
                >
                  {displayName(current)}
                </h2>
                <SocialIcons
                  social={current.social}
                  visible={socialDisplay === 'always' || isNameHovered}
                  iconSize={16}
                />
              </div>
              <p
                className="w-full mb-[32px] text-lg18 font-main font-[500] text-[var(--BlackBrand)] leading-[1.5] text-pretty max-w-[72ch]
                max-mobile:text-base max-mobile:mb-[16px] max-mobile:max-w-[100%]"
              >
                {current.description}
              </p>
              <div
                className="columns-2 gap-[2em] w-full
                max-tablet:columns-1 max-tablet:gap-0"
              >
                <p
                  className="text-base font-main font-[400] text-[var(--BlackBrand-br-5)] leading-[1.6] whitespace-pre-line
                  max-mobile:text-base"
                >
                  {current.bio}
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Image column — image + nav buttons stacked. Image keeps its 4/5 ratio at 50dvh;
            buttons sit beneath, aligned to the image's left edge. The wrapper is mx-auto
            so the whole stack stays centered in the grid cell as before. */}
        <div className="flex flex-col mx-auto">
          {/* All advisor images are mounted as stacked siblings; we toggle which is
              visible via opacity-100/0 instead of swapping the same <img>'s src.
              That avoids the "old image lingers during entrance animation" bug
              where the browser's per-IMG decode delay would let the previous
              slide's photo paint at the center as opacity rose 0→1. */}
          <div
            ref={imageRef}
            className="relative h-[50dvh] aspect-[4/5] rounded-[0.5em] overflow-hidden will-change-transform bg-[var(--WhiteBrand-br-95)]"
          >
            {advisors.map((a, i) => (
              <img
                key={i}
                src={displayImage(a)}
                alt={i === currentIndex ? displayName(a) : ''}
                decoding="async"
                fetchPriority={i === currentIndex ? 'high' : 'low'}
                aria-hidden={i !== currentIndex}
                className={`absolute inset-0 w-full h-full object-cover object-top ${
                  i === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
          </div>

          {/* Nav buttons — under the image, aligned to its left edge */}
          <div className="pt-[24px] flex items-center justify-end gap-[1.5em]">
            <button
              aria-label="Previous advisor"
              onClick={goPrev}
              className="h-[2.5em] w-[2.5em] bg-[var(--WhiteBrand-br-100)] shadow-[inset_0_0_0_0.09735em_var(--WhiteBrand-br-90)] rounded-full
              relative flex items-center justify-center overflow-hidden group
              cursor-pointer transition-all duration-300 active:scale-95 hover:shadow-[inset_0_0_0_0.09735em_var(--WhiteBrand-br-80)]"
            >
              <div
                className="text-[var(--GoldBrand-dr-3)] rotate-180
                group-hover:transition-all group-hover:duration-300 group-hover:-translate-x-8 group-hover:opacity-0"
              >
                <SectionArrow className="w-[1.25em] h-[1.25em]" />
              </div>
              <div
                className="text-[var(--GoldBrand-dr-3)] rotate-180 absolute translate-x-8 opacity-0
                group-hover:transition-all group-hover:duration-300 group-hover:ease-in-out group-hover:translate-x-0 group-hover:opacity-100 group-hover:delay-150"
              >
                <SectionArrow className="w-[1.25em] h-[1.25em]" />
              </div>
            </button>

            <button
              aria-label="Next advisor"
              onClick={goNext}
              className="h-[2.5em] w-[2.5em] bg-[var(--WhiteBrand-br-100)] shadow-[inset_0_0_0_0.09735em_var(--WhiteBrand-br-90)] rounded-full
              relative flex items-center justify-center overflow-hidden group
              cursor-pointer transition-all duration-300 active:scale-95 hover:shadow-[inset_0_0_0_0.09735em_var(--WhiteBrand-br-80)]"
            >
              <div
                className="text-[var(--GoldBrand-dr-3)]
                group-hover:transition-all group-hover:duration-300 group-hover:ease-in-out group-hover:translate-x-8 group-hover:opacity-0"
              >
                <SectionArrow className="w-[1.25em] h-[1.25em]" />
              </div>
              <div
                className="text-[var(--GoldBrand-dr-3)] absolute -translate-x-8 opacity-0
                group-hover:transition-all group-hover:duration-300 group-hover:ease-in-out group-hover:translate-x-0 group-hover:opacity-100 group-hover:delay-150"
              >
                <SectionArrow className="w-[1.25em] h-[1.25em]" />
              </div>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Slide stage — tablet + mobile (defaults = tablet side-by-side; max-mobile overrides stack) */}
      {isMobile && (
        <div className="grid max-tablet:grid-cols-[1fr_0.5fr] max-mobile:grid-cols-[1fr_1fr] mt-[2dvh]
        max-mobile:gap-x-0">
          {/* Image — col 1 row 1 on tablet; full width row 1 on mobile.
              Stacked siblings (one <img> per advisor); active one toggled via
              opacity. Same fix as the desktop image stage above. */}
          <div
            ref={imageRef}
            className="col-start-1 col-span-1 row-start-1 relative aspect-[4/5] h-[30dvh] rounded-[0.5em] overflow-hidden will-change-transform mt-[24px] bg-[var(--WhiteBrand-br-95)]
            max-mobile:col-span-2 max-mobile:h-[30dvh]"
          >
            {advisors.map((a, i) => (
              <img
                key={i}
                src={displayImage(a)}
                alt={i === currentIndex ? displayName(a) : ''}
                decoding="async"
                fetchPriority={i === currentIndex ? 'high' : 'low'}
                aria-hidden={i !== currentIndex}
                className={`absolute inset-0 w-full h-full object-cover object-top ${
                  i === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
          </div>

          {/* Name — col 1 row 2 on tablet; full width row 2 on mobile */}
          <div
            ref={mobileNameRef}
            className="col-start-1 col-span-1 row-start-2 mt-[16px] flex items-end gap-[0.5em] flex-wrap will-change-transform
            max-mobile:col-span-2"
            onMouseEnter={() => setIsNameHovered(true)}
            onMouseLeave={() => setIsNameHovered(false)}
          >
            {/* Reserves 2 lines of height (2 × 1.2em line-height = 2.4em) so
                the row stays the same height whether the active name is 1 or
                2 lines, preventing the buttons/description row from jumping
                between revealed (e.g. "Rachel Shields Williams" — sometimes
                1 line on wider phones) and secret variants. */}
            <h2
              className={`text-lg32 font-deco font-[580] leading-[1.2] text-[var(--BlackBrand)]
              max-mobile:text-lg20 max-mobile:min-h-[2.4em] ${
                current.revealed === false ? 'max-mobile:max-w-[8em] max-mobile:translate-y-[1em]' : ''
              }`}
            >
              {displayName(current)}
            </h2>
            <SocialIcons
              social={current.social}
              visible={socialDisplay === 'always' || isNameHovered}
              iconSize={14}
            />
          </div>

          {/* Standalone index — col 2 row 2 on tablet, centered. Hidden on mobile (the buttons row has its own embedded index there). */}
          <span className="col-start-2 col-span-1 row-start-2 self-center justify-self-center text-base font-main text-[var(--BlackBrand-br-5)] tracking-[0.05em] mt-[16px]
          max-mobile:hidden">
            {String(currentIndex + 1).padStart(2, '0')} /{' '}
            {String(advisors.length).padStart(2, '0')}
          </span>

          {/* Description — col 1 row 3 on tablet; full width row 3 on mobile.
              Stacking grid: ghost (tallest advisor's description) locks the
              row height so secret cards don't collapse and shift the buttons. */}
          <div
            ref={mobileDescRef}
            className="col-start-1 col-span-1 row-start-3 will-change-transform mt-[16px] grid
            max-mobile:col-span-2"
          >
            <p
              aria-hidden="true"
              className="[grid-area:1/1] invisible pointer-events-none text-lg18 font-main font-[500] leading-[1.5] text-balance
              max-tablet:text-pretty
              max-mobile:text-base max-mobile:text-pretty"
            >
              {tallestAdvisor.description}
            </p>
            <p className="[grid-area:1/1] text-lg18 font-main font-[500] text-[var(--BlackBrand)] leading-[1.5] text-balance
            max-tablet:text-pretty
            max-mobile:text-base max-mobile:text-pretty">
              {current.description}
            </p>
          </div>

           {/* Buttons row — prev / index / next.
               Tablet: col 2 row 3, group centered with tight gap. Mobile: full-width row 4, spread to edges. */}
           <div className="col-start-2 col-span-1 row-start-3 self-center w-full flex flex-row items-center min-h-[60px] justify-center gap-[24px] z-[1]
           max-mobile:col-start-1 max-mobile:col-span-2 max-mobile:row-start-4 max-mobile:self-auto max-mobile:items-center max-mobile:justify-between max-mobile:gap-0">
            <button
              aria-label="Previous advisor"
              onClick={goPrev}
              className="h-[60px] w-[60px] rounded-full
              relative flex items-center justify-center overflow-hidden group max-tablet:shadow-[inset_0_0_0_0.09735em_var(--WhiteBrand-br-90)]
              cursor-pointer transition-all duration-300 active:scale-95
              max-mobile:shadow-none"
            >
              <div
                className="text-[var(--GoldBrand-dr-3)] rotate-180
                group-hover:transition-all group-hover:duration-300 group-hover:-translate-x-8 group-hover:opacity-0"
              >
                <SectionArrow className="w-[1.5em] h-[1.5em]" />
              </div>
              <div
                className="text-[var(--GoldBrand-dr-3)] rotate-180 absolute translate-x-8 opacity-0
                group-hover:transition-all group-hover:duration-300 group-hover:ease-in-out group-hover:translate-x-0 group-hover:opacity-100 group-hover:delay-150"
              >
                <SectionArrow className="w-[1.5em] h-[1.5em]" />
              </div>
            </button>
            <span className="hidden max-mobile:inline text-base font-main text-[var(--BlackBrand-br-5)] tracking-[0.05em]">
              {String(currentIndex + 1).padStart(2, '0')} /{' '}
              {String(advisors.length).padStart(2, '0')}
            </span>
            <button
              aria-label="Next advisor"
              onClick={goNext}
              className="h-[60px] w-[60px] rounded-full
              relative flex items-center justify-center overflow-hidden group max-tablet:shadow-[inset_0_0_0_0.09735em_var(--WhiteBrand-br-90)]
              cursor-pointer transition-all duration-300 active:scale-95
              max-mobile:shadow-none"
            >
              <div
                className="text-[var(--GoldBrand-dr-3)]
                group-hover:transition-all group-hover:duration-300 group-hover:ease-in-out group-hover:translate-x-8 group-hover:opacity-0"
              >
                <SectionArrow className="w-[1.5em] h-[1.5em]" />
              </div>
              <div
                className="text-[var(--GoldBrand-dr-3)] absolute -translate-x-8 opacity-0
                group-hover:transition-all group-hover:duration-300 group-hover:ease-in-out group-hover:translate-x-0 group-hover:opacity-100 group-hover:delay-150"
              >
                <SectionArrow className="w-[1.5em] h-[1.5em]" />
              </div>
            </button>
          </div>

           {/* Bio — full width. Row 4 on tablet, row 5 on mobile.
               Stacking grid: ghost (tallest advisor's bio) locks the row
               height so secret cards don't collapse the layout below. */}
           <div
            ref={mobileBioRef}
            className="col-span-2 row-start-4 will-change-transform mt-[16px] grid
            max-mobile:row-start-5"
          >
            <p
              aria-hidden="true"
              className="[grid-area:1/1] invisible pointer-events-none text-base font-main font-[400] leading-[1.6] text-pretty whitespace-pre-line"
            >
              {tallestAdvisor.bio}
            </p>
            <p className="[grid-area:1/1] text-base font-main font-[400] text-[var(--BlackBrand-br-5)] leading-[1.6] text-pretty whitespace-pre-line">
              {current.bio}
            </p>
          </div>
        </div>
      )}

      {/* Indicator names — bottom row (desktop only; tablet/mobile use the in-row 01 / 03).
          Hidden for now — too many slides are still secret to make this row useful.
          Remove the leading `hidden` class once at least 3 advisors are revealed. */}
      <div
        className="grid grid-flow-col auto-cols-fr gap-[1.5em] mt-[32px]
        max-tablet:hidden"
      >
        {advisors.map((a, i) => {
          const active = i === currentIndex;
          return (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="flex flex-col items-start gap-[0.75em] text-left group"
              aria-label={`Go to ${displayIndexName(a)}`}
            >
              <div className="h-[0.09735em] w-full bg-[var(--WhiteBrand-br-90)] overflow-hidden">
                <div
                  className="h-full bg-[var(--GoldBrand-dr-2)] transition-all duration-500 ease-out"
                  style={{ width: active ? '100%' : '0%' }}
                />
              </div>
              <span
                className={`font-deco text-md font-[550] leading-[1.3] transition-colors ${
                  active
                    ? 'text-[var(--BlackBrand)]'
                    : 'text-[var(--BlackBrand-br-8)] group-hover:text-[var(--BlackBrand)]'
                }`}
              >
                {displayIndexName(a)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdvisorCarusel;
